import {
	DetailsRowCompat as DetailsRow,
	IColumn,
	IDetailsRowBaseProps,
	IDetailsRowProps,
	useTheme,
} from 'components/FluentUICompat';
import React, {useEffect, useMemo} from 'react';
import {getListClassNames} from 'styles';
import {WorkbenchHeader} from '..';
import {useDashboardQueries} from 'features/Dashboard/hooks/useDashboardQueries';
import {LoadWrapper} from 'components/LoadWrapper';
import {DashboardTooltipTranslationProvider} from '../DashboardTooltipTranslationProvider';
import {createHrefFromRegulation} from 'features/Regulations/components/RegulationsList/regulationsListLinks.utils';
import {EntityList} from 'components';
import {useVkoApproverWorkbenchStyles} from './useVkoApproverWorkbenchStyles';
import {useDashboardRenderers} from 'features/Dashboard/hooks/useDashboardRenderers';
import {VkoApproverTileProps} from './VkoApproverMyTodoTile';
import {useVkoApproverWorkbenchUtils} from './useVkoApproverWorkbenchUtils';

export const VkoApproverInputTile: React.FC<VkoApproverTileProps> = ({
	halfHeight,
	title,
	t,
	itemCountLoaded,
}) => {
	const theme = useTheme();
	const classNames = getListClassNames(theme, halfHeight);

	const {getVkoApproverInputItems} = useDashboardQueries();
	const {data, loading} = getVkoApproverInputItems();
	const {renderRegulatoryDocumentColumnField, sortByCreationDate} =
		useVkoApproverWorkbenchUtils();
	const {
		widthSTD80,
		widthSTD120,
		widthSTD180,
		classNames: vkoApproverClassNames,
	} = useVkoApproverWorkbenchStyles();
	const {renderWorkflowStatus, renderApprover} = useDashboardRenderers();

	useEffect(() => {
		if (!loading && data && itemCountLoaded) {
			itemCountLoaded(data.length);
		}
	}, [loading, data, itemCountLoaded]);

	const sortedData = useMemo(() => {
		return data ? sortByCreationDate(data.slice(0), false) : [];
	}, [data, sortByCreationDate]);

	const columns = useMemo(
		() => [
			{
				key: 'name',
				name: t('RegulatoryDocument'),
				fieldName: 'name',
				...widthSTD180,
				onRender: renderRegulatoryDocumentColumnField,
				sortable: true,
				customSorting(
					items: any[],
					column: IColumn,
					isSortedDescending?: boolean,
				) {
					return sortByCreationDate(items.slice(0), !isSortedDescending);
				},
			},
			{
				key: 'approver',
				name: t('Approver'),
				fieldName: 'workflow.clearingVKO',
				...widthSTD120,
				onRender: renderApprover,
				sortable: true,
			},
			{
				key: 'workflow.status',
				name: t('WorkflowStatus'),
				fieldName: 'workflow.status',
				...widthSTD120,
				onRender: renderWorkflowStatus,
				sortable: true,
			},
		],
		[
			renderApprover,
			renderRegulatoryDocumentColumnField,
			renderWorkflowStatus,
			sortByCreationDate,
		],
	);

	const itemList = useMemo(
		() => (
			<DashboardTooltipTranslationProvider>
				<EntityList
					styles={{root: {maxWidth: '240px'}}}
					sticky={false}
					selectionMode={0}
					items={sortedData}
					columns={columns}
					onShouldVirtualize={() => true}
					onRenderRow={(props?: IDetailsRowProps) => (
						<a href={`/regulatoryDocuments/${props?.item?.id}`}>
							<DetailsRow {...(props as IDetailsRowProps)} />
						</a>
					)}
				/>
			</DashboardTooltipTranslationProvider>
		),
		[columns, sortedData],
	);

	return (
		<div className={vkoApproverClassNames.roundedDashboardActivityContainer}>
			<WorkbenchHeader
				halfHeight
				title={title}
				items={data.length}
				hideNewWindowIcon={true}
			/>
			{loading ? (
				<LoadWrapper loading={true} />
			) : (
				<div className={classNames.dashboardActivityContent} data-is-scrollable>
					{itemList}
				</div>
			)}
		</div>
	);
};
